<template>
  <TresGroup name="FlowMapProvider">
    <slot />
  </TresGroup>
</template>

<script setup lang="ts">
// BASED ON OGL FLOW MAP
// https://github.com/oframe/ogl/blob/master/src/extras/Flowmap.js

import { Scene } from "three"
import { useFBO } from "@tresjs/cientos"
import { uuid } from "vue-uuid"
import fsTriangle from "@/components/three/fsTriangle"

const props = defineProps({
  size: {
    type: Number,
    default: 128,
  },
  falloff: {
    type: Number,
    default: 0.3,
  },
  alpha: {
    type: Number,
    default: 1.0,
  },
  dissipation: {
    type: Number,
    default: 0.985,
  },
  debug: {
    type: Boolean,
    default: false,
  },
})

const { renderer } = useTresContext()
const { onLoop } = useRenderLoop()

const windowSize = useWindowSize()

const refDebugMesh = ref(null)
const refDebugMaterial = ref(null)

// **********************************************************************
// FBO
// **********************************************************************

const fboSettings = {
  format: RGBAFormat,
  samples: 0,
  stencilBuffer: false,
  depthBuffer: false,
  minFilter: LinearFilter,
  type: FloatType,
}

const fbo1 = new WebGLRenderTarget(props.size, props.size, fboSettings)
const fbo2 = new WebGLRenderTarget(props.size, props.size, fboSettings)

const fbos = {
  read: fbo1,
  write: fbo2,
}

const swap = () => {
  const temp = fbos.read
  fbos.read = fbos.write
  fbos.write = temp
}

const tFlowMap = shallowRef({ value: fbo1.textures[0] })

provide("tFlowMap", tFlowMap)

// **********************************************************************
// SHADER
// **********************************************************************
const shader = {
  uniforms: {
    tMap: { value: fbos.read.textures[0] },

    uTime: { value: 0 },
    uFalloff: { value: props.falloff * 0.5 },
    uAlpha: { value: props.alpha },
    uDissipation: { value: props.dissipation },

    uAspect: { value: 1 },
    uMouse: { value: new Vector2() },
    uVelocity: { value: new Vector2() },
  },
  vertexShader: /*glsl*/ `
    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
      vUv = uv;
      vPosition = position;

      vec3 transformed = position;

      // float scale = 0.9;
      // mat4 sPos = mat4(vec4(scale,0.0,0.0,0.0),
      //                  vec4(0.0,scale,0.0,0.0),
      //                  vec4(0.0,0.0,1.0,0.0),
      //                  vec4(0.0,0.0,0.0,1.0));
      // transformed = (sPos * vec4(transformed, 1.0)).xyz;
      
      gl_Position = vec4( transformed, 1.0 );
    }
  
  `,
  fragmentShader: /*glsl*/ `

    uniform sampler2D tMap;

    uniform float uTime;
    uniform float uFalloff;
    uniform float uAlpha;
    uniform float uDissipation;

    uniform float uAspect;
    uniform vec2 uMouse;
    uniform vec2 uVelocity;

    varying vec2 vUv;

    void main() {

      vec2 colorUv = vUv;
      vec4 color = texture2D(tMap, colorUv) * uDissipation;

      

      vec2 cursor = vUv - uMouse;
      cursor.x *= uAspect;

      // Stamp
      vec3 stamp = vec3(uVelocity * vec2(1, -1), 1.0 - pow(1.0 - min(1.0, length(uVelocity)), 3.0));
      float falloff = smoothstep(uFalloff, 0.0, length(cursor)) * uAlpha;
      color.rgb = mix(color.rgb, stamp, vec3(falloff));

      gl_FragColor.rgb = color.rgb;
      // gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
    }
  `,
}

watchEffect(() => {
  fbo1.setSize(props.size, props.size)
  fbo2.setSize(props.size, props.size)
  shader.uniforms.uFalloff.value = props.falloff * 0.5
  shader.uniforms.uAlpha.value = props.alpha
  shader.uniforms.uDissipation.value = props.dissipation
})

// **********************************************************************
// ASPECT
// **********************************************************************

watchEffect(() => {
  shader.uniforms.uAspect.value =
    windowSize.width.value / windowSize.height.value
})

// **********************************************************************
// SCENE + CAMERA
// **********************************************************************

const vScene = new Scene()
vScene.name = uuid.v1()

const vMesh = new Mesh(fsTriangle, new ShaderMaterial(shader))
vScene.add(vMesh)

// const vCamera = new PerspectiveCamera()
const vCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1000)
vCamera.position.set(0, 0, 1000)
vScene.add(vCamera)

// **********************************************************************
// MOUSE
// **********************************************************************
const lastMouse = new Vector2()
const velocity = new Vector2(0, 0)
const mouse = new Vector2(-1, -1)
let lastTime

onMounted(() => {
  const isTouchCapable = "ontouchstart" in window
  if (isTouchCapable) {
    window.addEventListener("touchstart", updateMouse, false)
    window.addEventListener("touchmove", updateMouse, false)
  } else {
    window.addEventListener("mousemove", updateMouse, false)
  }
})

const updateMouse = (evt) => {
  // Mouse in px
  const sx = evt.changedTouches?.[0]?.clientX || evt.clientX
  const sy = evt.changedTouches?.[0]?.clientY || evt.clientY

  // Mouse in 0 to 1
  const x = sx / windowSize.width.value
  const y = 1 - sy / windowSize.height.value

  mouse.set(x, y)

  // Velocity
  if (!lastTime) {
    lastTime = performance.now()
    lastMouse.set(sx, sy)
  }

  const deltaX = sx - lastMouse.x
  const deltaY = sy - lastMouse.y

  lastMouse.set(sx, sy)

  let time = performance.now()

  let delta = Math.max(14, time - lastTime)
  lastTime = time

  velocity.x = deltaX / delta
  velocity.y = deltaY / delta

  velocity.needsUpdate = true
}

onUnmounted(() => {
  const isTouchCapable = "ontouchstart" in window
  if (isTouchCapable) {
    window.removeEventListener("touchstart", updateMouse, false)
    window.removeEventListener("touchmove", updateMouse, false)
  } else {
    window.removeEventListener("mousemove", updateMouse, false)
  }
})

// **********************************************************************
// RENDER
// **********************************************************************
onLoop(({ elapsed }) => {
  // MOUSE
  if (!velocity.needsUpdate) {
    mouse.set(-1, -1)
    velocity.set(0, 0)
  }
  velocity.needsUpdate = false

  shader.uniforms.uMouse.value.copy(mouse)
  shader.uniforms.uVelocity.value.lerp(velocity, velocity.length() ? 0.5 : 0.1)

  // SWAP FBOS
  shader.uniforms.tMap.value = fbos.read.textures[0]
  if (props.debug) {
    refDebugMaterial.value.map = fbos.read.textures[0]
    refDebugMaterial.value.needsUpdate = true
  }
  swap()

  // RENDER
  const oldAutoClear = renderer.value.autoClear

  renderer.value.autoClear = false
  renderer.value.setRenderTarget(fbos.read)
  renderer.value.render(vScene, vCamera)

  renderer.value.setRenderTarget(null)
  renderer.value.autoClear = oldAutoClear

  tFlowMap.value = fbos.read.textures[0]
})

defineExpose({
  tFlowMap,
})
</script>
